import { useCallback, useMemo } from "react";
import { useCdn } from "../../../hooks/caching/cdn/use-cdn";
import { ShapeLibraryData, ShapeMapping } from "frontend/canvas-designer-new/shape-libraries/types";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";
import FallbackThumbnails from "../fallback-data/thumbnails.json";
import FallbackMappings from "../fallback-data/mapping.json";

export function useShapeLibrary(id: string, useFallbackData: boolean = false) {
  const fallbackThumbnails = useFallbackData ? FallbackThumbnails : undefined;
  const fallbackMappings = useFallbackData ? FallbackMappings : undefined;

  const {
    data: thumbnails,
    isLoading: thumbnailsLoading,
    error: thumbnailsError,
    mutate: mutateThumbnails,
  } = useCdn(`/shape-libraries/${id}/thumbnails.json`, "json", fallbackThumbnails);

  const {
    data: mappings,
    isLoading: mappingLoading,
    error: mappingError,
    mutate: mutateMappings,
  } = useCdn(`/shape-libraries/${id}/mapping.json`, "json", fallbackMappings);

  const memoizedMappings = useDeepEqualMemo(mappings as ShapeMapping | undefined);
  const items = useMemo(
    () => new Set(Object.keys(memoizedMappings ?? (useFallbackData ? FallbackMappings : {}))),
    [memoizedMappings, useFallbackData]
  );

  const mutate = useCallback(() => {
    mutateThumbnails();
    mutateMappings();
  }, [mutateThumbnails, mutateMappings]);

  return {
    data: { items, thumbnails, mappings } as ShapeLibraryData,
    isLoading: thumbnailsLoading || mappingLoading,
    error: thumbnailsError || mappingError,
    mutate,
  };
}
